import {useEffect, useRef, useState, useMemo} from 'react';

import axios from 'axios';
import {toast} from 'react-toastify';

import {useForm, Controller} from 'react-hook-form';
import CalendarMobileView, {
  CalendarApiService
} from '../calendar/CalendarMobileView';
import CalendarDesktopView from '../calendar/CalendarDesktopView';
import {OrderData, FormValues, CityOption, Doctor} from '../../type';
import CitySelect from '../city/CitySelect';
import DoctorsSelect from '../doctors/DoctorsSelect';
import DoctorsSelectMobile from '../doctors/DoctorsSelectMobile';
import {API_ROUTE, HOST} from '../../config';
import Address from '../address/Address';
import YourChoice from '../yourChoice/YourChoice';
import SymptomsField from '../symptomsField/SymptomsField';
import {formatDateTime} from '../../utils/dateTimeFormatter';

import styles from './Form.module.scss';
import GroupButton from '../groupButton/GroupButton';

import MedicalDocumentationField from '../fileUpload/MedicalDocumentationField';
import {Button} from '../ui/button';
import Stepper from '../stepper/Stepper';

import ExclamationIcon from '../../assets/exclamation.svg?react';
import SmallTextField from '../smallTextField/SmallTextField';

export default function Form() {
  const [productId, setProductId] = useState<number>();
  const [citiesOption, setCitiesOption] = useState<CityOption[]>([]);
  const [addressOption, setAddressOption] = useState<CityOption[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [schedule, setSchedule] = useState<CalendarApiService[]>([]);
  const [isCitySelected, setIsCitySelected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [slotError, setSlotError] = useState<string | null>(null);

  const [isSendingData, setIsSendingData] = useState<boolean>(false);
  const [showAcceptHint, setShowAcceptHint] = useState<boolean>(false);
  const [showAcceptDateHint, setShowAcceptDateHint] = useState<boolean>(false);

  const hasFetchedProduct = useRef(false);
  const hasFetchedDoctors = useRef(false);
  const hasSetInitialDate = useRef(false);
  const hasSetInitialCity = useRef(false);
  const hasSetInitialDoctor = useRef(false);
  const isInitialCityFromUrl = useRef(false);
  const topRef = useRef<HTMLDivElement | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);
  const acceptRef = useRef<HTMLDivElement | null>(null);
  const acceptDateRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      if (
        topRef.current &&
        typeof topRef.current.scrollIntoView === 'function'
      ) {
        topRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
      if (
        typeof window !== 'undefined' &&
        typeof window.scrollTo === 'function'
      ) {
        window.scrollTo({top: 0, behavior: 'smooth'});
      }
    } catch (err) {
      // no-op
    }
  }, [currentStep]);

  const isProduct = (...ids: number[]) =>
    typeof productId === 'number' && ids.includes(productId);

  // Мемоізуємо початкові дані для швидшого завантаження
  const initialData = useMemo(() => {
    const formElement = document.getElementById('custom-api-react');
    const productDataAttr = formElement?.getAttribute('data-product-data');

    if (productDataAttr) {
      try {
        return JSON.parse(productDataAttr);
      } catch (err) {
        console.error('Error parsing product data from HTML:', err);
        return null;
      }
    }
    return null;
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {isSubmitting, isSubmitted}
  } = useForm<FormValues>({
    defaultValues: {
      selected_city: {id: '', name_city: '', office_id: ''},
      selected_doctor: {id: '', name: ''},
      selected_date: new Date(),
      selected_time: {time: '', address: '', id: '', desc: ''},
      bothers_you: '',
      chronic_diseases: '',
      selected_chronic_diseases: 'NIE',
      selected_medications: 'NIE',
      medications: '',
      selected_psychiatric_treatment: 'NIE',
      selected_allergies: 'NIE',
      allergies: '',
      selected_ikp: 'NIE',
      accept: false
    }
  });

  const selectedCity = watch('selected_city');
  const selectedDoctor = watch('selected_doctor');
  const selectedDate = watch('selected_date');
  const selectedTime = watch('selected_time');
  const selected_medical_documentation = watch(
    'selected_medical_documentation'
  );
  
  const selected_symptoms = watch('selected_symptoms');
  const bothersYou = watch('bothers_you');
  const selected_chronic_diseases = watch('selected_chronic_diseases');
  const chronicDiseases = watch('chronic_diseases');
  const selected_medications = watch('selected_medications');
  const selected_psychiatric_treatment = watch('selected_psychiatric_treatment');
  const selected_allergies = watch('selected_allergies');
  const medicalFiles = watch('documentation_files');
  const anotherLocationMedicalFiles = watch(
    'another_location_documentation_files'
  );
  const accept = watch('accept');
  const acceptDate = watch('accept_date');
  const selected_ikp = watch('selected_ikp');

  // If user accepts, hide the hint
  useEffect(() => {
    if (accept) {
      setShowAcceptHint(false);
    }
  }, [accept]);

  // If user accepts, hide the hint
  useEffect(() => {
    if (acceptDate) {
      setShowAcceptDateHint(false);
    }
  }, [acceptDate]);

  // Check if all requirements except "accept" are satisfied
  const requirementsMetExceptAccept =
    !!selectedCity?.name_city &&
    !!selectedTime?.time &&
    (!isProduct(41862, 45091, 54997, 43615, 65852, 65854) || !!bothersYou) &&
    (!isProduct(41862, 43615, 65854) || !!chronicDiseases) &&
    (!isProduct(41863) || selected_symptoms !== 'TAK' || !!bothersYou) &&
    (!isProduct(41862, 45091, 54997, 41863, 65852) ||
      selected_medical_documentation !== 'TAK' ||
      (Array.isArray(medicalFiles) && medicalFiles.length > 0)) &&
    (!isProduct(43615, 65854) ||
      selected_ikp === 'TAK' ||
      (Array.isArray(anotherLocationMedicalFiles) &&
        anotherLocationMedicalFiles.length > 0)) &&
    !isSubmitting &&
    !isSendingData;

  const isDisabledOnlyBecauseAcceptMissing =
    requirementsMetExceptAccept && !accept;

  // Відстежуємо вибір міста
  useEffect(() => {
    if (selectedCity && selectedCity.id && selectedCity.name_city) {
      setIsCitySelected(true);
    } else {
      setIsCitySelected(false);
    }
  }, [selectedCity]);


  // Автоматичний вибір першої доступної дати
  useEffect(() => {
    if (
      schedule &&
      Array.isArray(schedule) &&
      schedule.length > 0 &&
      selectedCity &&
      selectedCity.office_id &&
      !hasSetInitialDate.current // Тільки один раз
    ) {
      try {
        const now = new Date();
        const availableDates = schedule
          .filter((item) => {
            const itemDate = new Date(item.Date);
            const isToday =
              itemDate.getFullYear() === now.getFullYear() &&
              itemDate.getMonth() === now.getMonth() &&
              itemDate.getDate() === now.getDate();

            if (!item.Rows) return false;

            const hasValidSlots = item.Rows.some((row) => {
              const officeMatch = selectedCity.office_id.includes(
                String(row.OfficeId)
              );
              const doctorMatch =
                !selectedDoctor.id ||
                selectedDoctor.id === '' ||
                String(row.DoctorId) === selectedDoctor.id;

              const time = new Date(row.Time);
              const timeValid = !isToday || time > now;

              return officeMatch && doctorMatch && timeValid;
            });

            return hasValidSlots;
          })
          .map((item) => item.Date) // Використовуємо item.Date замість new Date(item.Date)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        if (availableDates.length > 0) {
          const firstAvailableDate = new Date(availableDates[0]); // Конвертуємо в Date для setValue

          hasSetInitialDate.current = true;
          setValue('selected_date', firstAvailableDate);
        }
      } catch (error) {
        console.error('Error in auto-select date:', error);
      }
    }
  }, [schedule, selectedCity, selectedDoctor, setValue]); // Видаляємо selectedDate з залежностей

  // Скидання hasSetInitialDate при зміні міста
  useEffect(() => {
    hasSetInitialDate.current = false;

    // При першому встановленні міста з URL, не скидаємо лікаря,
    // якщо в URL є параметр doctor — він буде встановлений окремим ефектом
    if (isInitialCityFromUrl.current) {
      isInitialCityFromUrl.current = false;
      setValue('accept_date', false);
      return;
    }

    setValue('selected_doctor', {id: '', name: 'Dowolny lekarz'});
    setValue('accept_date', false);
  }, [selectedCity?.id]);

  // Скидання вибраного часу при зміні лікаря
  useEffect(() => {
    // Скидаємо час при зміні лікаря/міста/дати, щоб уникнути конфліктів
    setValue('selected_time', {
      time: '',
      address: '',
      id: '',
      idsSameName: '',
      officeSameNameDoctors: '',
      desc: ''
    });
    setValue('accept_date', false);
  }, [selectedDate, selectedDoctor, selectedCity, setValue]);

  // Обчислюємо множину доступних лікарів (є слоти у вибраному місті)
  const availableDoctorIds = useMemo(() => {
    try {
      if (!selectedCity?.office_id || !Array.isArray(schedule)) {
        return new Set<string>();
      }
      const officeIds = selectedCity.office_id
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter(Boolean);
      const ids = new Set<string>();
      for (const day of schedule) {
        if (!day?.Rows) continue;
        for (const row of day.Rows) {
          if (officeIds.includes(row.OfficeId)) {
            ids.add(String(row.DoctorId));
          }
        }
      }
      return ids;
    } catch (e) {
      return new Set<string>();
    }
  }, [schedule, selectedCity?.office_id]);

  console.log('selectedDoctor:', doctors);
  console.log('availableDoctorIds:', availableDoctorIds);

  const onSubmit = async (data: FormValues) => {
    console.log(data, 'data -------');
    setIsSendingData(true);
    // Конвертація дати
    let selectedDateFormatted;
    if (data.selected_date instanceof Date) {
      const year = data.selected_date.getFullYear();
      const month = String(data.selected_date.getMonth() + 1).padStart(2, '0');
      const day = String(data.selected_date.getDate()).padStart(2, '0');
      selectedDateFormatted = `${year}-${month}-${day}`;
    } else {
      selectedDateFormatted = data.selected_date;
    }
    const body = {
      selected_doctor: data.selected_doctor.name
        ? data.selected_doctor.name
        : 'Dowolny lekarz',
      selected_doctor_id: data.selected_doctor.id
        ? data.selected_doctor.id
        : String(data.selected_time.doctorId),
      selected_city: data.selected_city.name_city,
      selected_city_id: data.selected_city.id,
      selected_city_oficeid: data.selected_city.office_id,
      selected_date: selectedDateFormatted,
      selected_time: data.selected_time.time,
      selected_cabinet: data.selected_time.address,
      selected_cabinet_id: data.selected_time.id,
      selected_cabinet_desk: data.selected_time.desc,
      selected_cabinet_ids_same_name: data.selected_time.idsSameName,
      selected_cabinet_same_name_doctors:
        data.selected_time.officeSameNameDoctors,
      selected_medical_documentation: data.selected_medical_documentation,
      another_location_documentation_files:
        data.another_location_documentation_files,
      documentation_files: data.documentation_files,
      selected_ikp: data.selected_ikp,
      bothers_you: data.bothers_you,
      chronic_diseases: data.chronic_diseases,
      selected_chronic_diseases: data.selected_chronic_diseases,
      selected_medications: data.selected_medications,
      medications: data.medications,
      selected_psychiatric_treatment: data.selected_psychiatric_treatment,
      selected_allergies: data.selected_allergies,
      allergies: data.allergies,
      selected_symptoms: data.selected_symptoms
    };
    await saveDate(body);
  };

  useEffect(() => {
    if (hasFetchedProduct.current) return;
    hasFetchedProduct.current = true;

    const formElement = document.getElementById('custom-api-react');
    const idFromData = formElement?.getAttribute('data-product-id');
    const idFromVars = (window as any).customApiVars?.productId;
    const resolvedId = idFromData || idFromVars;

    // Спробуємо отримати дані з HTML data-атрибута
    if (initialData) {
      if (initialData.city_options && Array.isArray(initialData.city_options)) {
        setCitiesOption(initialData.city_options);
      }
      if (
        initialData.address_options &&
        Array.isArray(initialData.address_options)
      ) {
        setAddressOption(initialData.address_options);
      }
      if (resolvedId) {
        setProductId(parseInt(resolvedId, 10));
      }
      setIsLoading(false);
      return; // Якщо дані є в HTML, не робимо API запит
    }

    // Fallback до API запиту якщо дані не знайдені в HTML
    const fetchProductId = async () => {
      try {
        const response = await axios.get(`${API_ROUTE}/get_product`, {
          withCredentials: true,
          headers: {'Content-Type': 'application/json'}
        });

        setCitiesOption(response.data.data_product.city_options);

        setAddressOption(response.data.data_product.address_options);
        const finalId = resolvedId || response.data.data_product.product_id;
        if (finalId) {
          setProductId(parseInt(finalId, 10));
        }
      } catch (err) {
        console.error('Error fetching product', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductId();
  }, [initialData]);

  useEffect(() => {
    if (hasFetchedDoctors.current) return;
    hasFetchedDoctors.current = true;

    // Спробуємо отримати дані лікарів з кешу або localStorage
    const cachedDoctors = localStorage.getItem('doctors_cache');
    if (cachedDoctors) {
      try {
        const doctors = JSON.parse(cachedDoctors);
        const cacheTime = localStorage.getItem('doctors_cache_time');
        const now = Date.now();

        // Кеш дійсний 5 хвилин
        if (cacheTime && now - parseInt(cacheTime) < 5 * 60 * 1000) {
          setDoctors(doctors);
          return;
        }
      } catch (err) {
        console.error('Error parsing cached doctors:', err);
      }
    }

    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${API_ROUTE}/get_doctor`, {
          withCredentials: true,
          headers: {'Content-Type': 'application/json'}
        });
        setDoctors(response.data.data_doctors);

        // Кешуємо дані
        localStorage.setItem(
          'doctors_cache',
          JSON.stringify(response.data.data_doctors)
        );
        localStorage.setItem('doctors_cache_time', Date.now().toString());
      } catch (err) {
        console.error('Error fetching doctors', err);
      }
    };
    fetchDoctors();
  }, []);

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  function getSchedule() {
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 39);
    const startDateStr = formatDate(today);
    const endDateStr = formatDate(endDate);

    // Спробуємо отримати розклад з кешу
    const cacheKey = `schedule_${productId}_${startDateStr}_${endDateStr}`;

    axios
      .get<{status: string; data: CalendarApiService[]}>(
        `${API_ROUTE}/get_list_schedule?date_start=${startDateStr}&date_end=${endDateStr}&id=${productId}`
      )
      .then((res) => {
        setSchedule(res.data.data);

        // Кешуємо розклад
        localStorage.setItem(cacheKey, JSON.stringify(res.data.data));
        localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      })
      .catch((err) => {
        console.error('Error fetching schedule:', err);
      });
  }

  const clearScheduleRefreshInterval = () => {
    if (refreshIntervalRef.current !== null) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  const startScheduleRefreshInterval = () => {
    clearScheduleRefreshInterval();
    refreshIntervalRef.current = window.setInterval(() => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible'
      ) {
        getSchedule();
      }
    }, 5 * 60 * 1000);
  };

  useEffect(() => {
    if (productId) {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + 39);
      const startDateStr = formatDate(today);
      const endDateStr = formatDate(endDate);

      // Спробуємо отримати розклад з кешу
      const cacheKey = `schedule_${productId}_${startDateStr}_${endDateStr}`;
      const cachedSchedule = localStorage.getItem(cacheKey);
      if (cachedSchedule) {
        try {
          const schedule = JSON.parse(cachedSchedule);
          const cacheTime = localStorage.getItem(`${cacheKey}_time`);
          const now = Date.now();

          // Кеш дійсний 0.5 хвилин для розкладу
          if (cacheTime && now - parseInt(cacheTime) < 1 * 30 * 1000) {
            setSchedule(schedule);
            return;
          }
        } catch (err) {
          console.error('Error parsing cached schedule:', err);
        }
      }

      getSchedule();
    }
  }, [productId]);

  // Автооновлення графіку кожні 5 хв. Зупинка, якщо вкладка прихована
  useEffect(() => {
    if (!productId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearScheduleRefreshInterval();
      } else {
        // При поверненні на вкладку одразу оновлюємо графік і перезапускаємо інтервал
        getSchedule();
        startScheduleRefreshInterval();
      }
    };

    if (document.visibilityState === 'visible') {
      startScheduleRefreshInterval();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearScheduleRefreshInterval();
    };
  }, [productId]);

  // Встановлюємо/утримуємо місто з параметра URL (?city=NAME)
  useEffect(() => {
    if (!Array.isArray(citiesOption) || citiesOption.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const cityParam = params.get('city');
      if (!cityParam) return;

      const normalized = decodeURIComponent(cityParam).trim().toLowerCase();
      const foundCity = citiesOption.find(
        (city) =>
          String(city?.name_city || '')
            .trim()
            .toLowerCase() === normalized
      );

      if (
        foundCity &&
        (!selectedCity?.id ||
          String(selectedCity.id) !== String((foundCity as any).id))
      ) {
        // Якщо в URL є параметр doctor, позначаємо що це початкове встановлення міста з URL,
        // щоб ефект скидання лікаря при зміні міста пропустив скидання
        const doctorParam = params.get('doctor');
        if (doctorParam) {
          isInitialCityFromUrl.current = true;
        }

        setValue('selected_city', {
          id: String((foundCity as any).id),
          name_city: (foundCity as any).name_city,
          office_id: String((foundCity as any).office_id)
        });
      }
    } catch (err) {
      // no-op
    }
  }, [citiesOption, setValue]);

  // Встановлюємо лікаря з параметра URL (?doctor=ID)
  // Чекаємо поки будуть завантажені doctors І встановлене місто (selectedCity)
  useEffect(() => {
    if (hasSetInitialDoctor.current) return;
    if (!Array.isArray(doctors) || doctors.length === 0) return;
    if (!selectedCity?.id) return;

    try {
      const params = new URLSearchParams(window.location.search);
      const doctorParam = params.get('doctor');
      if (!doctorParam) return;

      const foundDoctor = doctors.find(
        (doc) => String(doc.id) === String(doctorParam)
      );

      if (foundDoctor) {
        hasSetInitialDoctor.current = true;
        setValue('selected_doctor', {
          id: String(foundDoctor.id),
          name: foundDoctor.name
        });
      }
    } catch (err) {
      // no-op
    }
  }, [doctors, selectedCity?.id, setValue]);

  async function saveDate(body: Omit<OrderData, 'product_id' | 'quantity'>) {
    if (!productId) {
      console.error('Product ID is not set');
      toast.error('Błąd: Brak identyfikatora produktu');
      return;
    }

    try {
      const wpNonce = (window as any).customApiVars?.nonce;
      if (!wpNonce) {
        throw new Error(
          'Brak tokenu zabezpieczającego. Odśwież stronę i spróbuj ponownie.'
        );
      }
      const orderData = {
        ...body,
        product_id: productId,
        quantity: 1,
        _wpnonce: wpNonce
      };
      const response = await axios.post(`${API_ROUTE}/add-to-cart`, orderData, {
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': wpNonce,
          'X-Requested-With': 'XMLHttpRequest'
        },
        timeout: 10000
      });
      if (response.data?.status === 'success') {
        toast.success('Dodano do koszyka! Przekierowuję...');
        setTimeout(() => {
          window.location.href = `${HOST}/umow-wizyte`;
        }, 10);
        setTimeout(() => {
          setIsSendingData(false);
        }, 1000);
      } else {
        throw new Error(
          response.data?.message || 'Nieprawidłowa odpowiedź serwera'
        );
      }
    } catch (error: any) {
      let errorMessage =
        'Wystąpił błąd podczas dodawania do koszyka. Spróbuj ponownie.';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      if (error?.response?.status === 403) {
        errorMessage = 'Sesja wygasła. Odśwież stronę i spróbuj ponownie.';
      } else if (error?.response?.status === 404) {
        errorMessage =
          'Nie znaleziono produktu. Odśwież stronę i spróbuj ponownie.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage =
          'Upłynął limit czasu połączenia. Sprawdź swoje połączenie i spróbuj ponownie.';
      } else if (error?.response?.status && error.response.status >= 500) {
        errorMessage = 'Błąd serwera. Prosimy spróbować później.';
      }
      toast.error(errorMessage);
      setIsSendingData(false);
    }
  }

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!selectedTime?.time) {
        setSlotError('Select a pickup time to continue');
        return;
      } else {
        setSlotError(null);
      }
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      getSchedule();
      console.log('getSchedule', selectedTime?.time);
    }
  };

  
  return (
    <div className='App' ref={topRef}>
      <Stepper currentStep={currentStep} />
      <div className='r-exclamation'>
        <ExclamationIcon />
        <p>Termin zostanie zarezerwowany po dokonaniu płatności</p>{' '}
      </div>

      {/* Крок 2: Анкета */}
      {currentStep === 2 && isCitySelected && selectedTime?.time && (
        <div className='step-container'>
          {selectedCity?.name_city && selectedTime?.time && (
            <div className={styles.mobileChoiceContainer}>
              <YourChoice
                city={selectedCity.name_city}
                address={selectedTime.address || ''}
                dateTime={formatDateTime(selectedDate, selectedTime.time)}
                doctorName={selectedDoctor?.name}
                onChange={handlePrevStep}
                button={true}
              />
            </div>
          )}
        </div>
      )}

      {(isSendingData || isLoading) && (
        <div className={styles['loading-indicator']}>
          <div className={styles['spinner']}></div>
        </div>
      )}

      

      <form className='r-form-single-product' onSubmit={handleSubmit(onSubmit)}>
        {!isLoading && (
          <>
            {/* Крок 1: Вибір терміну */}
            {currentStep === 1 && (
              <div className='step-container'>
                <Controller
                  name='selected_city'
                  control={control}
                  render={({field}) => (
                    <CitySelect {...field} citiesOption={citiesOption} />
                  )}
                />

                {isCitySelected && (
                  <div className='r-form-fields-container r-form-fields-containers'>
                    {/*START ID 41863 */}
                    {isProduct(41862, 41863, 43615, 45091, 54997, 65852, 65854) && (
                      <Controller
                        name='selected_doctor'
                        control={control}
                        render={({field}) => (
                          <>
                            <div className='doctor-mobile'>
                              <DoctorsSelectMobile
                                value={field.value}
                                onChange={field.onChange}
                                officeId={selectedCity?.office_id}
                                doctors={doctors}
                                availableDoctorIds={availableDoctorIds}
                              />
                            </div>
                            <div className='doctor-desktop'>
                              <DoctorsSelect
                                value={field.value}
                                onChange={field.onChange}
                                officeId={selectedCity?.office_id}
                                doctors={doctors}
                                availableDoctorIds={availableDoctorIds}
                              />
                            </div>
                          </>
                        )}
                      />
                    )}
                    {/*END ID 41863 */}

                    <Controller
                      name='selected_date'
                      control={control}
                      render={({field}) => (
                        <>
                          <div className='calendar-mobile'>
                            <CalendarMobileView
                              {...field}
                              productId={productId}
                              officeId={selectedCity?.office_id}
                              doctorId={selectedDoctor?.id}
                              schedule={schedule}
                            />
                          </div>
                          <div className='calendar-desktop'>
                            <CalendarDesktopView
                              {...field}
                              productId={productId}
                              officeId={selectedCity?.office_id}
                              doctorId={selectedDoctor?.id}
                              schedule={schedule}
                            />
                          </div>
                        </>
                      )}
                    />

                    <Controller
                      name='selected_time'
                      control={control}
                      render={({field}) => {
                        let filteredSchedule = schedule;

                        if (
                          selectedCity?.office_id &&
                          Array.isArray(schedule)
                        ) {
                          const officeIds = selectedCity.office_id
                            .split(',')
                            .map((id) => parseInt(id.trim(), 10))
                            .filter(Boolean);

                          filteredSchedule = schedule.filter((item) => {
                            const hasMatchingRows = item.Rows.some((row) => {
                              const officeMatch = officeIds.includes(
                                row.OfficeId
                              );
                              const doctorMatch =
                                !selectedDoctor?.id ||
                                selectedDoctor.id === '' ||
                                String(row.DoctorId) ===
                                  String(selectedDoctor.id);

                              return officeMatch && doctorMatch;
                            });

                            return hasMatchingRows;
                          });
                        }

                        return (
                          <>
                            <Address
                              {...field}
                              productId={productId}
                              officeIds={selectedCity?.office_id}
                              doctorId={selectedDoctor?.id}
                              addressOption={addressOption}
                              schedule={filteredSchedule}
                              selectedDate={selectedDate}
                              doctors={doctors}
                              onChange={field.onChange}
                            />
                            {slotError && (
                              <div
                                style={{
                                  color: 'red',
                                  marginTop: 8,
                                  fontSize: 14
                                }}
                              >
                                {slotError}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />

                    {selectedCity?.name_city && selectedTime?.time && (
                      <YourChoice
                        city={selectedCity.name_city}
                        address={selectedTime.address || ''}
                        dateTime={formatDateTime(
                          selectedDate,
                          selectedTime.time
                        )}
                        doctorName={selectedDoctor?.name}
                        onChange={() => {
                          console.log('Change selection clicked');
                        }}
                        button={false}
                      />
                    )}

                    <Controller
                      name='accept_date'
                      control={control}
                      rules={{
                        required:
                          'Zaznacz wymagane zgody'
                      }}
                      render={({field, fieldState}) => (
                        <div className={styles.checkboxRow} style={{alignItems: 'center'}} ref={acceptDateRef}>
                          <input
                            type='checkbox'
                            id='accept_date'
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            className={`${styles.checkbox} ${
                              fieldState.error ? styles.checkboxInvalid : ''
                            }`}
                          />
                          <label
                            htmlFor='accept_date'
                            className={styles.checkboxLabel}
                            style={{fontSize: "15px"}}
                          >
                            Potwierdzam, że <strong>sprawdziłem/am poprawność wybranego terminu</strong> oraz godziny wizyty.
                          </label>
                          {fieldState.error && (
                            <span className={styles.checkboxError}>
                              {fieldState.error.message}
                            </span>
                          )}

                          {!acceptDate && showAcceptDateHint && (
                            <span className={styles.checkboxError}>
                              Zaznacz wymagane zgody
                            </span>
                          )}
                        </div>
                      )}
                    />

                    <div
                      style={{position: 'relative'}}
                    >
                      <button
                        type='button'
                        className={styles['next-step-button']}
                        onClick={handleNextStep}
                        disabled={
                          !selectedCity?.name_city ||
                          !selectedTime?.time ||
                          !acceptDate
                        }
                      >
                        Dalej
                      </button>
                      {!acceptDate && (
                        <div
                          onClick={() => {
                            setShowAcceptDateHint(true);
                            try {
                              acceptDateRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                              });
                            } catch (e) {
                              // no-op
                            }
                          }}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            cursor: 'pointer',
                            background: 'transparent'
                          }}
                          aria-hidden='true'
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Крок 2: Анкета */}
            {currentStep === 2 && isCitySelected && selectedTime?.time && (
              <div className='step-container'>
                {/*START ID 41862, 45091, 54997,65852, 43615, 65854 */}
                {isProduct(41862, 45091, 54997, 65852, 43615, 65854) && (
                  <div className='r-form-fields-container'>
                    <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                      Opisz swoje dolegliwości i objawy
                      <span className='text-destructive'>*</span>
                    </h3>
                    <div className='mt-2'>
                      <SymptomsField control={control} name='bothers_you' />
                    </div>
                  </div>
                )}

                {/*START ID 41862, 43615, 65854*/}
                {isProduct(41862, 43615, 65854) && (
                  <>
                    <div className='r-form-fields-container'>
                      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                        Czy chorujesz na choroby przewlekłe?
                        <span className='text-destructive'>*</span>
                      </h3>

                      <GroupButton
                          control={control}
                          name='selected_chronic_diseases'
                          options={[
                            {label: 'TAK', value: 'TAK'},
                            {label: 'NIE', value: 'NIE'}
                          ]}
                          defaultValue='NIE'
                          required
                        />
                    </div>

                    {selected_chronic_diseases === 'TAK' && (
                      <div className='mt-2'>
                        <SmallTextField control={control} name='chronic_diseases' placeholder='Podaj jakie choroby przewlekłe' />
                      </div>
                    )}
                  </>
                )}

                {/*START ID 41862, 43615 65854 */}
                {isProduct(41862, 43615, 65854) && (
                  <>
                    <div className='r-form-fields-container'>
                      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                        Czy przyjmujesz na stałe jakieś leki lub stosujesz inne substancje (np. suplementy, używki)?
                        <span className='text-destructive'>*</span>
                      </h3>

                      <GroupButton
                          control={control}
                          name='selected_medications'
                          options={[
                            {label: 'TAK', value: 'TAK'},
                            {label: 'NIE', value: 'NIE'}
                          ]}
                          defaultValue='NIE'
                          required
                        />
                    </div>

                    {selected_medications === 'TAK' && (
                      <div className='mt-2'>
                        <SmallTextField control={control} name='medications' placeholder='Podaj jakie leki lub substancje' />
                      </div>
                    )}
                  </>
                )}

                {/*START ID 41862, 65854 */}
                {isProduct(41862, 65854) && (
                  <>
                    <div className='r-form-fields-container'>
                      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                        Czy jesteś obecnie w trakcie leczenia psychiatrycznego lub leczyłeś/aś się psychiatrycznie w przeszłości?
                        <span className='text-destructive'>*</span>
                      </h3>

                      <GroupButton
                          control={control}
                          name='selected_psychiatric_treatment'
                          options={[
                            {label: 'TAK', value: 'TAK'},
                            {label: 'NIE', value: 'NIE'}
                          ]}
                          defaultValue='NIE'
                          required
                        />
                    </div>
                  </>
                )}

                {/*START ID 41862, 43615, 65854 */}
                {isProduct(41862, 43615, 65854) && (
                  <>
                    <div className='r-form-fields-container'>
                      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                        Czy masz alergie lub nadwrażliwości?
                        <span className='text-destructive'>*</span>
                      </h3>

                      <GroupButton
                          control={control}
                          name='selected_allergies'
                          options={[
                            {label: 'TAK', value: 'TAK'},
                            {label: 'NIE', value: 'NIE'}
                          ]}
                          defaultValue='NIE'
                          required
                        />
                    </div>

                    {selected_allergies === 'TAK' && (
                      <div className='mt-2'>
                        <SmallTextField control={control} name='allergies' placeholder='Podaj jakie alergie lub nadwrażliwości' />
                      </div>
                    )}
                  </>
                )}



                {/*START ID 41862, 45091, 54997, 65852, 41863 */}
                {isProduct(41862, 45091, 54997, 65852, 41863) && (
                  <>
                    <div className='r-form-fields-container'>
                      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                        Chcesz dołączyć dokumentację medyczną?
                        <span className='text-destructive'>*</span>
                      </h3>

                      <GroupButton
                        control={control}
                        name='selected_medical_documentation'
                        options={[
                          {label: 'TAK', value: 'TAK'},
                          {label: 'NIE', value: 'NIE'}
                        ]}
                        defaultValue='NIE'
                        required
                      />
                    </div>

                    {selected_medical_documentation === 'TAK' && (
                      <MedicalDocumentationField
                        control={control}
                        name='documentation_files'
                        maxFiles={5}
                        title='Dodaj dokumentację medyczną'
                        uploadUrl='/wp-json/api_front/v1/upload_file_tmp'
                        required={true}
                        minFiles={1}
                        requiredMessage='Dodaj co najmniej 1 plik'
                      />
                    )}
                  </>
                )}
                {/*END ID 41862, 45091, 54997, 65852, 41863 */}

                {/*START ID 41862, 45091, 54997, 65852, 43615, 65854 */}
                {isProduct(41862, 45091, 54997, 65852, 43615, 65854) && (
                  <div className='r-form-fields-container'>
                    <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                      Czy dodałeś naszą placówkę do swojego IKP?
                      <span className='text-destructive'>*</span>
                    </h3>

                    <GroupButton
                      control={control}
                      name='selected_ikp'
                      options={[
                        {label: 'TAK', value: 'TAK'},
                        {label: 'NIE', value: 'NIE'}
                      ]}
                      required
                    />
                  </div>
                )}
                {/*END ID 41862, 45091, 54997, 65852, 43615, 65854 */}

                {/*START ID 43615, 65854 */}
                {isProduct(43615, 65854) && (
                  <MedicalDocumentationField
                    control={control}
                    name='another_location_documentation_files'
                    maxFiles={5}
                    required={selected_ikp === 'NIE'}
                    title='Dołącz pełną dokumentację medyczną z dotychczasowego leczenia w innej placówce.'
                    uploadUrl='/wp-json/api_front/v1/upload_file_tmp'
                    requiredMessage='Dodaj co najmniej 1 plik'
                  />
                )}
                {/*END ID 43615, 65854 */}

                

                {/*START ID 41863 */}
                {isProduct(41863) && (
                  <>
                    <div className='r-form-fields-container'>
                      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                        Сzy w ostatnim czasie pojawily sie u Ciebie jakies nowe,
                        niepokojace objawy, ktorych nie konsultowalas/es z
                        lekarzem?
                        <span className='text-destructive'>*</span>
                      </h3>

                      <GroupButton
                        control={control}
                        name='selected_symptoms'
                        options={[
                          {label: 'TAK', value: 'TAK'},
                          {label: 'NIE', value: 'NIE'}
                        ]}
                        required
                      />
                    </div>

                    {selected_symptoms === 'TAK' && (
                      <div className='r-form-fields-container'>
                        <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-lg'>
                          Opisz swoje dolegliwości i objawy
                          <span className='text-destructive'>*</span>
                        </h3>
                        <div className='mt-2'>
                          <SymptomsField control={control} name='bothers_you' />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {/*END ID 41863 */}

                {selectedCity?.name_city && selectedTime?.time && (
                  <div className={styles.desctopChoiceContainer}>
                    <YourChoice
                      city={selectedCity.name_city}
                      address={selectedTime.address || ''}
                      dateTime={formatDateTime(selectedDate, selectedTime.time)}
                      doctorName={selectedDoctor?.name}
                      onChange={handlePrevStep}
                      button={true}
                    />
                  </div>
                )}

                <Controller
                  name='accept'
                  control={control}
                  rules={{
                    required:
                      'Musisz zaakceptować regulamin i politykę prywatności'
                  }}
                  render={({field, fieldState}) => (
                    <div className={styles.checkboxRow} ref={acceptRef}>
                      <input
                        type='checkbox'
                        id='accept'
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className={`${styles.checkbox} ${
                          fieldState.error ? styles.checkboxInvalid : ''
                        }`}
                      />
                      <label htmlFor='accept' className={styles.checkboxLabel}>
                        Potwierdzam że zapoznałem się i akceptuje
                        <a
                          href='https://ziolo24.pl/regulamin/'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {' '}
                          regulaminem{' '}
                        </a>
                        oraz
                        <a
                          href='https://ziolo24.pl/polityka-prywatnosci/'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {' '}
                          politykę prywatności{' '}
                        </a>
                        portalu oraz ich postanowienia. Potwierdzam że podane
                        przeze mnie informacje i dane są prawdziwe. Korzystam z
                        usługi we własnym imieniu i z własnej woli.
                      </label>
                      {fieldState.error && (
                        <span className={styles.checkboxError}>
                          {fieldState.error.message}
                        </span>
                      )}

                      {!accept && showAcceptHint && (
                        <span className={styles.checkboxError}>
                          Zaznacz wymagane zgody
                        </span>
                      )}
                    </div>
                  )}
                />

                <div className={styles.stepButtons}>
                  <button
                    type='button'
                    className={styles['prev-step-button']}
                    onClick={handlePrevStep}
                  >
                    wróć
                  </button>

                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <button
                      type='submit'
                      className={styles['submit-button']}
                      disabled={
                        !selectedCity?.name_city ||
                        !selectedTime?.time ||
                        !accept ||
                        // Якщо продукт 41862, 45091 або 43615 — поле bothers_you завжди обов'язкове
                        // (isProduct(41862, 45091, 54997, 43615) &&
                        //   !bothersYou) ||
                        // // Якщо продукт 41863 — поле bothers_you обов'язкове тільки якщо selected_symptoms === 'TAK'
                        // (isProduct(41863) &&
                        //   selected_symptoms === 'TAK' &&
                        //   !bothersYou) ||
                        // (isProduct(41862, 45091, 54997, 41863) &&
                        //   selected_medical_documentation === 'TAK' &&
                        //   !(
                        //     Array.isArray(medicalFiles) &&
                        //     medicalFiles.length > 0
                        //   )) ||
                        // (isProduct(43615) &&
                        //   !(
                        //     Array.isArray(anotherLocationMedicalFiles) &&
                        //     anotherLocationMedicalFiles.length > 0
                        //   )) ||
                        isSubmitting ||
                        isSendingData
                      }
                    >
                      Dalej
                    </button>
                    {isDisabledOnlyBecauseAcceptMissing && (
                      <div
                        onClick={() => {
                          setShowAcceptHint(true);
                          try {
                            acceptRef.current?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'center'
                            });
                          } catch (e) {
                            // no-op
                          }
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          cursor: 'pointer',
                          background: 'transparent'
                        }}
                        aria-hidden='true'
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
}
